import {
  type PlanCounterpartyMismatchPartner,
  PlanCounterpartyMismatchSide,
} from '@ebalance/proto/bffuser/v1/submission_management_model_pb'

import type { PlanTableMismatchHighlight } from '~/components/planSubmit/PlanTable'
import type {
  BreakdownColumn,
  ColumnSide,
} from '~/pages/ProcurementPlanDetail/models/columns'

/** 列の照合に使う最小フィールド */
type MatchableColumn = Pick<BreakdownColumn, 'counterpartyCode' | 'fixedLabel'>

/** 行 index の範囲 (0-origin、`fromIndex` 包含・`toIndexExclusive` 排他) */
export type RowRange = { fromIndex: number; toIndexExclusive: number }

/**
 * 不整合の箇所 1 件 (不整合バナーの 1 件。issue #10317)。
 * 側 × 取引先コード × 連続コマ範囲で 1 件。範囲を特定できない取引先は `range` 無しの 1 件。
 */
export type MismatchItem = {
  side: ColumnSide
  counterpartyCode: string
  /**
   * 表示名。JEPX 市場列に当たる件は列の市場名 (列見出しと同じ名前)、取引先列に当たる件は
   * 通知の取引先名、取引先名が届かない時は取引先コード
   */
  counterpartyName: string
  /** 範囲。通知がコマを特定しない時は undefined (取引先の列見出しだけを強調し、セル・時刻セル・合計列は強調しない) */
  range: RowRange | undefined
}

/**
 * 列が取引先コードで照合される時のコード。取引先列は `counterpartyCode`、JEPX 市場列は
 * 固定コード (JSPT3 / J1HR3)。0251 の取引先行は BG 単位で JEPX も 1 取引先として届くため、
 * JEPX 列も同じ規則で当てる (mock-app も JEPX 列をコードで照合する)。
 */
export function columnMatchCode(column: MatchableColumn): string | undefined {
  return column.counterpartyCode ?? column.fixedLabel?.code
}

/**
 * 取引先コードに当たる列の index をすべて返す (取引毎表示の複数契約列、JEPX スポット調達の
 * 2 列)。不整合の常時強調 (AC-PPD-009〜013) と deeplink の一時強調 (AC-PPD-022) の照合を
 * この 1 つに揃える。
 */
export function matchingColumnIndexes(
  columns: readonly MatchableColumn[],
  counterpartyCode: string,
): number[] {
  const indexes: number[] = []
  for (const [index, column] of columns.entries()) {
    if (columnMatchCode(column) === counterpartyCode) indexes.push(index)
  }
  return indexes
}

/**
 * 取引先の名前。空文字は名前なしとして扱う (呼び出し側はコードへ fallback する)。
 * 解消ダイアログの取引先一覧と不整合バナーで同じ判定を使う。
 */
export function partnerDisplayName(
  partner: Pick<PlanCounterpartyMismatchPartner, 'counterpartyName'>,
): string | undefined {
  const name = partner.counterpartyName
  return name !== undefined && name !== '' ? name : undefined
}

/**
 * 件の表示名。JEPX 市場列 (固定ラベルの列) に当たる件は列の市場名を使う。通知の取引先名は
 * JP06367 の区分 (「相対契約分」等) になりうるが、件は区分の 2 列をまとめて指すので
 * 列見出しと揃う市場名を出す。
 */
function itemDisplayName(
  partner: PlanCounterpartyMismatchPartner,
  column: MatchableColumn,
): string {
  if (column.fixedLabel !== undefined) return column.fixedLabel.primary
  return partnerDisplayName(partner) ?? partner.counterpartyCode
}

function toColumnSide(
  side: PlanCounterpartyMismatchSide,
): ColumnSide | undefined {
  if (side === PlanCounterpartyMismatchSide.PROCUREMENT) return 'procurement'
  if (side === PlanCounterpartyMismatchSide.SALES) return 'sales'
  return undefined
}

const SIDE_ORDER: Record<ColumnSide, number> = { procurement: 0, sales: 1 }

/**
 * `partners[]` (未解消の取引相手間ミスマッチの取引先) と表示中の列から、不整合の箇所を組む
 * (issue #10317)。
 *
 * - 1 件 = 側 × 取引先コード × `ranges[]` の 1 要素。`ranges` が空の取引先は範囲なしの 1 件
 * - 表示中の列に当たらない取引先 (種別で事業者を選んで列が隠れた、版に該当取引先の列が無い)
 *   は除く (AC-PPD-013 注記 / AC-PPD-026 注記)
 * - 並びは表示列の左から (調達 → 販売、各側は最初に当たる列の順)、同じ取引先はコマ順
 *
 * 不整合かどうかは backend の判定 (`partners[]`) をそのまま使い、画面で再判定しない。
 */
export function buildMismatchItems(params: {
  partners: readonly PlanCounterpartyMismatchPartner[]
  procurementColumns: readonly MatchableColumn[]
  salesColumns: readonly MatchableColumn[]
}): MismatchItem[] {
  const columnsBySide: Record<ColumnSide, readonly MatchableColumn[]> = {
    procurement: params.procurementColumns,
    sales: params.salesColumns,
  }
  const located: Array<{ item: MismatchItem; columnIndex: number }> = []
  for (const partner of params.partners) {
    const side = toColumnSide(partner.side)
    if (side === undefined) continue
    const columns = columnsBySide[side]
    const [columnIndex] = matchingColumnIndexes(
      columns,
      partner.counterpartyCode,
    )
    const column = columnIndex === undefined ? undefined : columns[columnIndex]
    if (columnIndex === undefined || column === undefined) continue
    const base = {
      side,
      counterpartyCode: partner.counterpartyCode,
      counterpartyName: itemDisplayName(partner, column),
    }
    const ranges = [...partner.ranges].sort((a, b) => a.fromKoma - b.fromKoma)
    if (ranges.length === 0) {
      located.push({ item: { ...base, range: undefined }, columnIndex })
      continue
    }
    for (const r of ranges) {
      located.push({
        // コマ番号 (1..48、両端含む) → 行 index (0-origin、終端排他)
        item: {
          ...base,
          range: { fromIndex: r.fromKoma - 1, toIndexExclusive: r.toKoma },
        },
        columnIndex,
      })
    }
  }
  // 安定 sort: 同じ取引先の範囲は上で並べたコマ順を保つ
  return located
    .sort(
      (a, b) =>
        SIDE_ORDER[a.item.side] - SIDE_ORDER[b.item.side] ||
        a.columnIndex - b.columnIndex,
    )
    .map((l) => l.item)
}

/**
 * 件の集合の同一性 (注目位置を保ってよいか) を判定する文字列。件の追加・除外・並びの
 * 変化で変わる。
 */
export function mismatchItemsSignature(items: readonly MismatchItem[]): string {
  return items
    .map(
      (i) =>
        `${i.side}|${i.counterpartyCode}|${i.range?.fromIndex ?? ''}|${i.range?.toIndexExclusive ?? ''}`,
    )
    .join('\n')
}

/** 強調の照合に使う列 (表示中の列) */
type KeyedColumn = MatchableColumn & Pick<BreakdownColumn, 'key'>

function addRows(
  map: Map<string, Set<number>>,
  key: string,
  range: RowRange,
): void {
  const rows = map.get(key) ?? new Set<number>()
  for (let i = range.fromIndex; i < range.toIndexExclusive; i += 1) rows.add(i)
  map.set(key, rows)
}

/**
 * 件を表示中の列 (表示モード適用後) の強調の集合にする (AC-PPD-009〜012)。当たる列は
 * すべて強調し、範囲なしの件は列見出しだけ (セル・時刻セル・合計列のセルは強調しない)。
 * `cellKey` は共通 PlanTable に渡す列 key (側で一意にしたもの)。
 */
export function resolveMismatchHighlight(params: {
  items: readonly MismatchItem[]
  columnsBySide: Record<ColumnSide, readonly KeyedColumn[]>
  totalKeyBySide: Record<ColumnSide, string>
  cellKey: (side: ColumnSide, columnKey: string) => string
}): PlanTableMismatchHighlight {
  const cellRows = new Map<string, Set<number>>()
  const headColumnKeys = new Set<string>()
  const totalRows = new Map<string, Set<number>>()
  const timeRows = new Set<number>()
  for (const item of params.items) {
    const columns = params.columnsBySide[item.side]
    const keys = matchingColumnIndexes(columns, item.counterpartyCode).flatMap(
      (index) => {
        const column = columns[index]
        return column === undefined
          ? []
          : [params.cellKey(item.side, column.key)]
      },
    )
    if (keys.length === 0) continue
    for (const key of keys) headColumnKeys.add(key)
    const { range } = item
    if (range === undefined) continue
    for (const key of keys) addRows(cellRows, key, range)
    addRows(totalRows, params.totalKeyBySide[item.side], range)
    for (let i = range.fromIndex; i < range.toIndexExclusive; i += 1) {
      timeRows.add(i)
    }
  }
  return { cellRows, headColumnKeys, totalRows, timeRows }
}

/**
 * 注目中の件の先頭セル (範囲の先頭行 × 当たる列のうち最も左) を返す (AC-PPD-016)。
 * 範囲なしの件は行 index を持たない (列見出しだけを表示領域へ入れる)。表示中の列に当たらない件は undefined。
 */
export function resolveMismatchFocusCell(params: {
  item: MismatchItem
  columnsBySide: Record<ColumnSide, readonly KeyedColumn[]>
  cellKey: (side: ColumnSide, columnKey: string) => string
}): { columnKey: string; rowIndex: number | undefined } | undefined {
  const { item } = params
  const columns = params.columnsBySide[item.side]
  const [first] = matchingColumnIndexes(columns, item.counterpartyCode)
  const column = first === undefined ? undefined : columns[first]
  if (column === undefined) return undefined
  return {
    columnKey: params.cellKey(item.side, column.key),
    // コマを特定できない件は行を持たない (列見出しだけを表示領域へ入れる)
    rowIndex: item.range?.fromIndex,
  }
}

/**
 * 共通 PlanTable の常時強調とフォーカスに渡す状態 (AC-PPD-009〜012 / 016)。閉じている間は
 * `items` が空。不整合バナーと同じ hook (`useMismatchNavigation`) が組む。
 */
export type MismatchTableState = {
  items: readonly MismatchItem[]
  /** 注目中の件と前へ / 次へを押した回数。注目なしは undefined */
  focus: { item: MismatchItem; navigationId: number } | undefined
}
